import { Typography } from '@material-ui/core'
import { uniqBy } from 'lodash'
import React, { useState } from 'react'
import { useRedirect } from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { PolicyRepaymentsList } from 'shared/components/Policies/PolicyRepaymentsList'
import { Text } from 'shared/components/Text/Text'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { AssociatedEquipmentInfo, Repayment, RepaymentsBreakdowns } from 'shared/models/repayments'
import { sizes } from 'shared/theme'
import { bgGray, lightGray2, midGray, primaryBlack, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { OldConsoleLink } from '../Aside/OldConsoleLink'

export function TeamRepayments({ record }: any) {
  const [selectedEquipment, setSelectedEquipment] = useState<string>('all')
  const repaymentsBreakdowns: RepaymentsBreakdowns | null = record.repaymentsBreakdowns ?? null

  return (
    <FlexBox isColumn alignItems="stretch" mb={4}>
      <FlexBox flex={1} mb={2}>
        <RepaymentBox
          title={`Advances (${repaymentsBreakdowns?.advance_breakdown.number_of_policies ?? 0})`}
          total={{
            title: 'Total remaining balance',
            amount: repaymentsBreakdowns?.advance_breakdown.total_remaining_balance ?? '0',
          }}
          underpayment={{
            title: 'Overdue balance',
            amount: repaymentsBreakdowns?.advance_breakdown.underpayment ?? '0',
          }}
        />
        <RepaymentBox
          title={`Recurring deductions (${repaymentsBreakdowns?.subscription_breakdown.number_of_policies})`}
          total={{
            title: 'Total remaining balance',
            amount: repaymentsBreakdowns?.subscription_breakdown.total_remaining_balance ?? '0',
          }}
          underpayment={{
            title: 'Overdue balance',
            amount: repaymentsBreakdowns?.subscription_breakdown.underpayment ?? '0',
          }}
        />
        <RepaymentBox
          title={`Escrow (${repaymentsBreakdowns?.escrow_breakdown.number_of_policies ?? 0})`}
          total={{
            title: 'Total amount owed',
            amount: repaymentsBreakdowns?.escrow_breakdown.total_remaining_balance ?? '0',
          }}
          underpayment={{
            title: 'Overdue balance',
            amount: repaymentsBreakdowns?.escrow_breakdown.underpayment ?? '0',
          }}
        />
      </FlexBox>
      <PoliciesList
        record={record}
        repayments={(repaymentsBreakdowns?.repayments ?? []).filter(
          (r) =>
            selectedEquipment === 'all' || r.repayment_details.equipment?.vin_number === selectedEquipment
        )}
        equipments={(repaymentsBreakdowns?.repayments ?? [])
          .map((r) => r.repayment_details.equipment)
          .filter((e): e is AssociatedEquipmentInfo => !!e)}
        selectedEquipment={selectedEquipment}
        setSelectedEquipment={setSelectedEquipment}
      />
    </FlexBox>
  )
}

const formatAmount = (amount?: string) =>
  !amount || isNaN(parseFloat(amount)) ? '-' : `$${parseFloat(amount).toFixed(2)}`

function RepaymentBox({
  title,
  total,
  underpayment,
}: {
  title: string
  total: { title: string; amount: string }
  underpayment: { title: string; amount: string }
}) {
  return (
    <FlexBox
      isColumn
      flex={1}
      styles={`
        max-width: 360px;
        background-color: ${bgGray};
        border-radius: 3px;
        padding: 20px 23px;
        margin: 30px 10px;
      `}
    >
      <Text variant="uppercaseLabel3" fontColor={midGray} mb={2.5}>
        {title}
      </Text>
      <FlexBox justifyContent="space-between" mb={0.5}>
        <Text variant="header3" fontColor={primaryBlack}>
          {total.title}
        </Text>
        <Text variant="header3" fontColor={primaryBlack}>
          {formatAmount(total.amount)}
        </Text>
      </FlexBox>
      <FlexBox justifyContent="space-between">
        <FlexBox>
          <div
            style={{
              height: 14,
              width: 17,
              borderLeft: `1px solid ${lightGray2}`,
              borderBottom: `1px solid ${lightGray2}`,
              margin: '0 6px 10px 10px',
            }}
          ></div>
          <Text variant="label2" fontColor={midGray}>
            {underpayment.title}
          </Text>
        </FlexBox>
        <Text variant="label2" fontColor={midGray}>
          {formatAmount(underpayment.amount)}
        </Text>
      </FlexBox>
    </FlexBox>
  )
}

const handleClickAdd = (basePath: string, team: any, redirect: any) => {
  redirect(`/teams/${team.id}/policy/add`, basePath, team.id, {}, {})
  return
}

function PoliciesList({
  record,
  repayments,
  equipments,
  selectedEquipment,
  setSelectedEquipment,
}: {
  record: any // team
  repayments: Repayment[]
  equipments: AssociatedEquipmentInfo[]
  selectedEquipment: string
  setSelectedEquipment: (equipment: string) => void
}) {
  const redirect = useRedirect()

  return (
    <>
      <Typography variant="h5">
        <FlexBox flex={1} justifyContent="space-between" mb={2}>
          <EquipmentDropdown
            label="Filter by equipment"
            options={[
              { value: 'all', label: 'All' },
              ...uniqBy(
                equipments.map((e) => ({
                  value: e.vin_number,
                  label: `${e.license_plate_state}-${e.license_plate_number} (${e.vin_number})`,
                })),
                'value'
              ),
            ]}
            value={selectedEquipment}
            onChange={setSelectedEquipment as HandlerOf<any>}
            labelColor={selectedEquipment === 'all' ? royalBlue : undefined}
            formatLabel={(label) => (selectedEquipment === 'all' ? 'Filter by truck' : label)}
            isFullWidth
            noMargin
          />

          <FlexBox gap="20px">
            <OldConsoleLink oldConsoleUrl={`/ct-admin/cash/team/${record.id}/view-team-liability/`} />

            <Button size={sizes.large} onClick={() => handleClickAdd('/teams', record, redirect)}>
              ADD DEDUCTION
            </Button>
          </FlexBox>
        </FlexBox>
      </Typography>

      <PolicyRepaymentsList
        repayments={repayments}
        onRowClick={(row: Repayment) =>
          window.open(`/teams/${record.id}/policy/details/${row?.repayment_details.policy_id}`)
        }
      />
    </>
  )
}

const EquipmentDropdown = styled(Dropdown)`
  .dropdown-select {
    justify-content: flex-start;
    gap: 20px;
  }
`
